import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { RoutePaths } from "../../../../enumStrings";
import {
  useGetCmpEventDetailQuery,
  useGetCmpEventTypesQuery,
  useUpdateCmpEventMutation,
  useGetCmpEventDraftQuery,
} from "../../../../graphql/types";
import { useCmpGlobalSettings } from "../../../../hooks/useCmpSettingsSection";
import useGetChamberFromUserIdInAuth from "../../../../hooks/useGetChamberFromUserIdInAuth";
import EventWizard from "../CreateEvent/wizard/EventWizard";
import { WizardAction } from "../CreateEvent/wizard/wizardShared";
import { ResultModal } from "../EventSales/eventSalesShared";
import {
  buildBlankEventFormState,
  eventFormStateToInput,
  eventReturnToFormState,
  toEventTypeOptions,
} from "../eventBackendMappers";
import { EventFormState } from "../eventMockData";
import { eventDraftToFormState } from "../eventDraftDocument";
import { normalizeCmpTimeZone } from "../../../../utils/cmpTimeZones";
import { formatEventScheduleParts } from "../eventDateTime";
import { getPreserveExpectedErrorsForCallerContext } from "../../../../utils/apolloErrorPolicy";
import {
  prepareEventDataForSave,
  getEventSaveErrorMessage,
} from "../eventSaveInput";
import { applyDuplicateEventSetup } from "./duplicateEventSetup";
import { STAFF_SUPPORT_EMAIL, UserFacingError } from "../../../../utils/userFacingError";

const eventUpdateErrorContext = getPreserveExpectedErrorsForCallerContext({
  expectedGraphQLErrorCodes: ["BAD_USER_INPUT"],
});

// The failure title names the button staff clicked (wave 3, audit EV-019).
const SAVE_FAILURE_TITLES: Record<WizardAction, string> = {
  draft: "We could not save the draft.",
  publish: "We could not publish the event.",
  save: "We could not update the event.",
  schedule: "We could not schedule the event.",
};

const getBrowserTimeZone = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Toronto";

const UpdateEvent = (): React.ReactElement => {
  const navigate = useNavigate();
  const location = useLocation();
  const draftId = new URLSearchParams(location.search).get("draftId");
  const { eventId } = useParams<{ eventId: string }>();
  // Create Event Save Draft lands here with its message and step.
  const arrivalState = location.state as { eventSavedMessage?: string; step?: number } | null;
  const [initialStep] = useState(() => arrivalState?.step || 1);
  const chamber = useGetChamberFromUserIdInAuth();
  const chamberId = chamber?.id || "";
  const [preparingSave, setPreparingSave] = useState(false);
  const saveInProgress = useRef(false);
  const [feedback, setFeedback] = useState<{
    success: boolean;
    title: string;
    message?: string;
    highlights?: string[];
  } | null>(() => arrivalState?.eventSavedMessage ? { success: true, title: arrivalState.eventSavedMessage } : null);
  useEffect(() => {
    // The message shows once; a reload or Back does not show it again.
    if (arrivalState?.eventSavedMessage) navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- arrival only
  }, []);
  const [lastSavedEvent, setLastSavedEvent] = useState<{
    eventId: string;
    event: EventFormState;
  } | null>(null);
  // The first real save (updateCmpEvent) ends the duplicate setup; the
  // backend clears duplicatedFromEventId in the same save.
  const [duplicateSetupSaved, setDuplicateSetupSaved] = useState(false);

  const { data: detailData, loading: detailLoading } =
    useGetCmpEventDetailQuery({
      variables: { input: { chamberId, eventId: eventId || "" } },
      skip: !chamberId || !eventId,
      fetchPolicy: "cache-and-network",
    });
  const { data: eventTypesData, loading: eventTypesLoading } =
    useGetCmpEventTypesQuery({
      variables: { input: { chamberId, includeArchived: true } },
      skip: !chamberId,
      fetchPolicy: "cache-and-network",
    });
  const { data: settingsData } = useCmpGlobalSettings(chamberId);
  const [updateCmpEvent, { loading: updatingEvent }] =
    useUpdateCmpEventMutation({ context: eventUpdateErrorContext });

  // A side draft saved before Oct 2026 (CMP_EVENT_DRAFT) still opens here;
  // the next save promotes it onto the event and deletes the side record.
  const { data: draftData, loading: draftLoading } = useGetCmpEventDraftQuery({
    variables: { input: { chamberId, eventId, draftId: draftId || "" } },
    skip: !chamberId || !eventId || !draftId, fetchPolicy: "network-only",
  });
  const eventTypes = useMemo(
    () => toEventTypeOptions(eventTypesData?.getCmpEventTypes.eventTypes || []),
    [eventTypesData?.getCmpEventTypes.eventTypes]
  );
  const timeZone = normalizeCmpTimeZone(
    detailData?.getCmpEventDetail.event?.timeZone ||
      settingsData?.getChamberMemberProSettings.setup.timeZone ||
      getBrowserTimeZone()
  ).timeZone;
  const loadedEvent = useMemo(() => {
    if (lastSavedEvent && lastSavedEvent.eventId === eventId) {
      return lastSavedEvent.event;
    }

    if (draftData?.getCmpEventDraft) return eventDraftToFormState(draftData.getCmpEventDraft);
    const event = detailData?.getCmpEventDetail.event;
    return event
      ? eventReturnToFormState(event, timeZone)
      : buildBlankEventFormState(eventTypes);
  }, [
    detailData?.getCmpEventDetail.event,
    draftData,
    eventId,
    eventTypes,
    lastSavedEvent,
    timeZone,
  ]);
  const { event: initialEvent, duplicated } = useMemo(
    () => applyDuplicateEventSetup({
      event: loadedEvent,
      record: duplicateSetupSaved ? null : detailData?.getCmpEventDetail.event,
    }),
    [detailData?.getCmpEventDetail.event, duplicateSetupSaved, loadedEvent]
  );

  const handleSubmit = async ({
    event,
    action,
    leaveAfterSave = false,
  }: {
    event: EventFormState;
    action: WizardAction;
    leaveAfterSave?: boolean;
  }) => {
    if (saveInProgress.current) return false;
    saveInProgress.current = true;
    setPreparingSave(true);
    setFeedback(null);
    try {
      if (!chamberId || !eventId) {
        throw new UserFacingError("This event is still loading. Wait a moment, then try again.");
      }
      // Claude Design CES onSaveDraft persistEvent('Draft') (Monday
      // 13290832990): Save Draft saves the event itself. A Draft, Cancelled
      // or Scheduled event is saved as Draft (decision B8, Oct 19 2026: a
      // Scheduled event leaves the publish schedule); a published event keeps
      // its status.
      const becomesDraft = event.status === "Draft" || event.status === "Cancelled" || event.status === "Scheduled";
      const draftStatus: EventFormState["status"] = becomesDraft ? "Draft" : event.status;
      const statusEvent: EventFormState = {
        ...event,
        status: action === "publish" ? "Published" : action === "schedule" ? "Scheduled" : action === "draft" ? draftStatus : event.status,
        // A Draft has no publish time (same as Unscheduled (Back to Draft)).
        ...(action === "draft" && event.status === "Scheduled" ? { publishAtUtcMilli: null } : {}),
      };
      const result = await updateCmpEvent({
        variables: {
          input: {
            chamberId,
            eventId,
            expectedUpdatedAtUtcMilli: event.updatedAtUtcMilli,
            draftId: event.draftId,
            expectedDraftRevision: event.draftRevision,
            data: await prepareEventDataForSave(
              eventFormStateToInput({ event: statusEvent, timeZone })
            ),
          },
        },
      });
      if (!result.data?.updateCmpEvent.eventSavedSuccessfully) {
        throw new Error("The event could not be updated.");
      }
      const savedEvent = result.data.updateCmpEvent.event;
      setDuplicateSetupSaved(true);
      if (leaveAfterSave) return true;
      // The side draft was promoted and deleted in the same save.
      if (draftId) navigate(RoutePaths.chamberAdmin_events_update.replace(":eventId", eventId), { replace: true });
      // The mutation response is the authoritative read-after-write result.
      // An immediate detail refetch can hit DynamoDB's eventually consistent
      // read path and remount the wizard with the pre-save time/options.
      setLastSavedEvent({
        eventId,
        event: savedEvent
          ? eventReturnToFormState(savedEvent, timeZone)
          : statusEvent,
      });
      const eventName = event.title.trim() || "Untitled Event";
      if (action === "publish") {
        setFeedback({ success: true, title: `The event ${eventName} has been published successfully and is now live on your website.`, highlights: [eventName] });
      } else if (action === "schedule") {
        const { date, time } = formatEventScheduleParts(statusEvent.publishAtUtcMilli || Date.now(), timeZone);
        setFeedback({ success: true, title: `The event ${eventName} has been scheduled to publish on ${date} at ${time}.`, highlights: [eventName, date, time] });
      } else if (action === "draft" && statusEvent.status === "Draft") {
        setFeedback({ success: true, title: "Event saved as draft." });
      } else {
        setFeedback({ success: true, title: `Event ${eventName} has been updated.`, highlights: [eventName] });
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
      return true;
    } catch (error) {
      setFeedback({
        success: false,
        title: SAVE_FAILURE_TITLES[action],
        message: getEventSaveErrorMessage(
          error,
          "Try again. If it keeps happening, email " + STAFF_SUPPORT_EMAIL + ".",
          { eventExists: true }
        ),
      });
      window.scrollTo({ top: 0, behavior: "smooth" });
      return false;
    } finally {
      saveInProgress.current = false;
      setPreparingSave(false);
    }
  };

  if (detailLoading || eventTypesLoading || draftLoading || !chamberId) {
    return (
      <div className="mx-auto w-full max-w-6xl rounded-lg border border-gray-200 bg-white px-5 py-4 text-sm font-semibold text-gray-600">
        Loading event setup...
      </div>
    );
  }

  if (!detailData?.getCmpEventDetail.event) {
    return (
      <div className="mx-auto max-w-3xl rounded-lg border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-900">
        <p className="font-bold">We could not find this event.</p>
        <p className="mt-1">
          It may have been deleted. Select Back to Manage Events and choose the
          event again.
        </p>
        <button
          type="button"
          onClick={() => navigate(RoutePaths.chamberAdmin_events_manage)}
          className="mt-4 rounded-lg bg-cpaGold px-4 py-2 text-sm font-bold text-white transition hover:bg-amber-500"
        >
          Back to Manage Events
        </button>
      </div>
    );
  }

  return (
    <>
      {/* Save/publish feedback uses the shared popup (EVT-PUBLISH-001) like
          the other event dashboard pages. */}
      <ResultModal
        open={Boolean(feedback)}
        success={feedback?.success ?? true}
        title={feedback?.title || ""}
        message={feedback?.message}
        highlights={feedback?.highlights}
        onClose={() => setFeedback(null)}
      />
      <EventWizard
        // A stable key keeps the current step after Update Event (Monday
        // extra task). The wizard resets its saved baseline itself and takes
        // the saved event (new updatedAtUtcMilli) while the form is clean
        // (EVT-PUBLISH-002).
        key={eventId}
        mode="update"
        initialEvent={initialEvent}
        eventTypes={eventTypes}
        chamberId={chamberId}
        timeZone={timeZone}
        saving={preparingSave || updatingEvent}
        duplicated={duplicated}
        initialStep={initialStep}
        onSubmit={handleSubmit}
      />
    </>
  );
};

export default UpdateEvent;
